// components/restaurant/FiltersSidebar.tsx
"use client";

import { useState, useEffect } from "react";
import {
  Filter,
  Search,
  DollarSign,
  MapPin,
  Clock,
  Star,
  X,
  SlidersHorizontal,
  Utensils,
  Users,
  Truck,
  Check,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

interface FilterState {
  search: string;
  priceRange: string[];
  distance: number;
  cuisines: string[];
  rating: number;
  deliveryTime: number;
  openNow: boolean;
  offers: boolean;
  features: string[];
  amenities: string[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

interface FiltersSidebarProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  availableCuisines: string[];
  availablePriceRanges: string[];
  availableFeatures: string[];
  availableAmenities: string[];
  className?: string;
  isMobile?: boolean;
  onClose?: () => void;
}

export function FiltersSidebar({
  filters,
  onFilterChange,
  availableCuisines,
  availablePriceRanges,
  availableFeatures,
  availableAmenities,
  className = "",
  isMobile = false,
  onClose,
}: FiltersSidebarProps) {
  const [localFilters, setLocalFilters] = useState(filters);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    price: true,
    cuisine: true,
    rating: true,
    features: false,
    amenities: false,
    sort: false,
  });

  const updateFilter = (key: keyof FilterState, value: any) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
    onFilterChange(updated);
  };

  const togglePriceRange = (range: string) => {
    const updated = localFilters.priceRange.includes(range)
      ? localFilters.priceRange.filter(r => r !== range)
      : [...localFilters.priceRange, range];
    updateFilter("priceRange", updated);
  };

  const toggleCuisine = (cuisine: string) => {
    const updated = localFilters.cuisines.includes(cuisine)
      ? localFilters.cuisines.filter(c => c !== cuisine)
      : [...localFilters.cuisines, cuisine];
    updateFilter("cuisines", updated);
  };

  const toggleFeature = (feature: string) => {
    const updated = localFilters.features.includes(feature)
      ? localFilters.features.filter(f => f !== feature)
      : [...localFilters.features, feature];
    updateFilter("features", updated);
  };

  const toggleAmenity = (amenity: string) => {
    const updated = localFilters.amenities.includes(amenity)
      ? localFilters.amenities.filter(a => a !== amenity)
      : [...localFilters.amenities, amenity];
    updateFilter("amenities", updated);
  };

  const clearAllFilters = () => {
    const cleared = {
      search: "",
      priceRange: [],
      distance: 50,
      cuisines: [],
      rating: 0,
      deliveryTime: 60,
      openNow: false,
      offers: false,
      features: [],
      amenities: [],
      sortBy: "rating",
      sortOrder: "desc" as const,
    };
    setLocalFilters(cleared);
    onFilterChange(cleared);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (localFilters.search) count++;
    if (localFilters.priceRange.length > 0) count++;
    if (localFilters.distance < 50) count++;
    if (localFilters.cuisines.length > 0) count++;
    if (localFilters.rating > 0) count++;
    if (localFilters.deliveryTime < 60) count++;
    if (localFilters.openNow) count++;
    if (localFilters.offers) count++;
    if (localFilters.features.length > 0) count++;
    if (localFilters.amenities.length > 0) count++;
    return count;
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const sortOptions = [
    { value: "rating", label: "Rating" },
    { value: "name", label: "Name" },
    { value: "created_at", label: "Date Added" },
    { value: "updated_at", label: "Last Updated" },
  ];

  useEffect(() => {
    if (isMobile) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isMobile]);

  const sidebarClasses = `
    ${isMobile ? 'fixed inset-0 z-50 bg-white p-6 overflow-y-auto' : 'lg:sticky lg:top-6 h-fit'}
    ${className}
  `;

  return (
    <div className={sidebarClasses}>
      {/* Mobile Header */}
      {isMobile && (
        <div className="flex items-center justify-between mb-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-700" />
            <h3 className="font-bold text-lg text-gray-900">Filters & Sort</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            aria-label="Close filters"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
      )}
      
      {/* Desktop Header */}
      {!isMobile && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-700" />
              <h3 className="font-bold text-lg text-gray-900">Filters & Sort</h3>
            </div>
            {getActiveFilterCount() > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-amber-600 hover:text-amber-700 font-medium"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="text-sm text-gray-500 mb-4">
            {getActiveFilterCount()} active filter{getActiveFilterCount() !== 1 ? 's' : ''}
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Search */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Search className="w-4 h-4" />
              Search
            </label>
          </div>
          <input
            type="text"
            value={localFilters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            placeholder="Restaurant name or cuisine..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Sort By */}
        <div className="space-y-3">
          <button
            onClick={() => toggleSection('sort')}
            className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
          >
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="w-4 h-4" />
              Sort By
            </div>
            {expandedSections.sort ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {expandedSections.sort && (
            <div className="space-y-2 pl-6">
              {sortOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                >
                  <input
                    type="radio"
                    name="sort"
                    value={option.value}
                    checked={localFilters.sortBy === option.value}
                    onChange={() => updateFilter("sortBy", option.value)}
                    className="w-4 h-4 text-amber-600"
                  />
                  <span className="text-sm text-gray-700">{option.label}</span>
                </label>
              ))}
              
              <div className="flex items-center gap-4 pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sortOrder"
                    checked={localFilters.sortOrder === 'asc'}
                    onChange={() => updateFilter("sortOrder", 'asc')}
                    className="w-4 h-4 text-amber-600"
                  />
                  <span className="text-sm text-gray-700">Ascending</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="sortOrder"
                    checked={localFilters.sortOrder === 'desc'}
                    onChange={() => updateFilter("sortOrder", 'desc')}
                    className="w-4 h-4 text-amber-600"
                  />
                  <span className="text-sm text-gray-700">Descending</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Price Range */}
        <div className="space-y-3">
          <button
            onClick={() => toggleSection('price')}
            className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
          >
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Price Range
            </div>
            {expandedSections.price ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {expandedSections.price && (
            <div className="flex flex-wrap gap-2 pl-6">
              {availablePriceRanges.map((range) => (
                <button
                  key={range}
                  onClick={() => togglePriceRange(range)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    localFilters.priceRange.includes(range)
                      ? "bg-amber-500 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Distance */}
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm font-medium text-gray-700">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Distance: {localFilters.distance}km
            </div>
          </label>
          <div className="px-2">
            <input
              type="range"
              min="1"
              max="50"
              value={localFilters.distance}
              onChange={(e) => updateFilter("distance", parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>1km</span>
              <span>25km</span>
              <span>50km</span>
            </div>
          </div>
        </div>

        {/* Cuisine */}
        <div className="space-y-3">
          <button
            onClick={() => toggleSection('cuisine')}
            className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
          >
            <div className="flex items-center gap-2">
              <Utensils className="w-4 h-4" />
              Cuisine ({localFilters.cuisines.length})
            </div>
            {expandedSections.cuisine ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {expandedSections.cuisine && (
            <div className="space-y-2 pl-6 max-h-48 overflow-y-auto">
              {availableCuisines.map((cuisine) => (
                <label
                  key={cuisine}
                  className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                >
                  <input
                    type="checkbox"
                    checked={localFilters.cuisines.includes(cuisine)}
                    onChange={() => toggleCuisine(cuisine)}
                    className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                  />
                  <span className="text-sm text-gray-700">{cuisine}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Rating */}
        <div className="space-y-3">
          <button
            onClick={() => toggleSection('rating')}
            className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
          >
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Minimum Rating
            </div>
            {expandedSections.rating ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          
          {expandedSections.rating && (
            <div className="pl-6">
              <div className="flex items-center gap-2 mb-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => updateFilter("rating", star)}
                    className={`p-1.5 rounded ${
                      localFilters.rating >= star
                        ? "text-amber-500"
                        : "text-gray-300 hover:text-gray-400"
                    }`}
                  >
                    <Star className={`w-5 h-5 ${localFilters.rating >= star ? 'fill-current' : ''}`} />
                  </button>
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {localFilters.rating > 0 ? `${localFilters.rating}+ stars` : "Any rating"}
              </span>
            </div>
          )}
        </div>

        {/* Delivery Time */}
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm font-medium text-gray-700">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Max Delivery Time: {localFilters.deliveryTime}min
            </div>
          </label>
          <div className="px-2">
            <input
              type="range"
              min="15"
              max="120"
              step="15"
              value={localFilters.deliveryTime}
              onChange={(e) => updateFilter("deliveryTime", parseInt(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-amber-500"
            />
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>15min</span>
              <span>60min</span>
              <span>120min</span>
            </div>
          </div>
        </div>

        {/* Features */}
        {availableFeatures.length > 0 && (
          <div className="space-y-3">
            <button
              onClick={() => toggleSection('features')}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
            >
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4" />
                Features ({localFilters.features.length})
              </div>
              {expandedSections.features ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {expandedSections.features && (
              <div className="space-y-2 pl-6 max-h-48 overflow-y-auto">
                {availableFeatures.map((feature) => (
                  <label
                    key={feature}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={localFilters.features.includes(feature)}
                      onChange={() => toggleFeature(feature)}
                      className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">{feature}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Amenities */}
        {availableAmenities.length > 0 && (
          <div className="space-y-3">
            <button
              onClick={() => toggleSection('amenities')}
              className="flex items-center justify-between w-full text-sm font-medium text-gray-700"
            >
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                Amenities ({localFilters.amenities.length})
              </div>
              {expandedSections.amenities ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            
            {expandedSections.amenities && (
              <div className="space-y-2 pl-6 max-h-48 overflow-y-auto">
                {availableAmenities.map((amenity) => (
                  <label
                    key={amenity}
                    className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={localFilters.amenities.includes(amenity)}
                      onChange={() => toggleAmenity(amenity)}
                      className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
                    />
                    <span className="text-sm text-gray-700">{amenity}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Toggles */}
        <div className="space-y-3">
          <label className="flex items-center justify-between text-sm font-medium text-gray-700">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Availability
            </div>
          </label>
          <div className="space-y-2 pl-6">
            <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
              <input
                type="checkbox"
                checked={localFilters.openNow}
                onChange={(e) => updateFilter("openNow", e.target.checked)}
                className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
              />
              <Clock className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-700">Open Now</span>
            </label>
            
            <label className="flex items-center gap-2 cursor-pointer p-2 hover:bg-gray-50 rounded">
              <input
                type="checkbox"
                checked={localFilters.offers}
                onChange={(e) => updateFilter("offers", e.target.checked)}
                className="w-4 h-4 text-amber-600 rounded border-gray-300 focus:ring-amber-500"
              />
              <Truck className="w-4 h-4 text-gray-600" />
              <span className="text-sm text-gray-700">Has Offers</span>
            </label>
          </div>
        </div>
      </div>

      {/* Mobile Apply Button */}
      {isMobile && (
        <div className="sticky bottom-0 pt-6 mt-6 border-t bg-white">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-600 transition-colors"
            >
              Apply Filters ({getActiveFilterCount()})
            </button>
            <button
              onClick={clearAllFilters}
              className="px-4 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}